import { useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { useGenericBi } from '../../lib/use-generic-bi';
import { useActiveConversation } from '../chat/ActiveConversationContext';
import { useVoiceCallSummary } from '../../lib/voice-call-presence-context';
import { formatElapsed } from '../../lib/voice-call-timer';
import { Button, Card } from './ui';
import styles from './VoiceCallIsland.module.css';

/**
 * The minimized island has TWO placements, decided purely by viewport width:
 *
 * - `floating` (desktop / side-chat) — a `position: fixed` pill centered at the top,
 *   riding above every screen. This is the original behaviour.
 * - `strip` (mobile web) — the same pill inside an in-flow strip above the app frame
 *   that RESERVES its own height and pushes the header + content down, so it never
 *   sits on top of a screen's header. Mirrors the native apps' collapsed-call banner.
 *
 * `none` when the call isn't minimized (or isn't up) — nothing renders in either spot.
 */
export type VoiceIslandPlacement = 'strip' | 'floating' | 'none';

export function voiceIslandPlacement(narrow: boolean, islandVisible: boolean): VoiceIslandPlacement {
  if (!islandVisible) return 'none';
  return narrow ? 'strip' : 'floating';
}

/**
 * The mobile-web strip's height (px), excluding the top safe-area inset. AppFrame
 * subtracts `--voice-island-h` (this + safe-area) from <main>'s minHeight so the
 * strip and <main> sum to the viewport instead of overflowing it — the same
 * one-constant-feeds-both discipline the app-download banner uses (lib/app-banner.ts).
 */
export const VOICE_STRIP_HEIGHT_PX = 64;

/**
 * The minimized call: a compact island that floats above every screen while the
 * call itself keeps running — audio, transcript and tool calls untouched. Tapping
 * the body puts the user back in the conversation the call belongs to; End hangs up.
 *
 * ## Shape and colour — Figma "Speak Mode" island (14037:155062)
 *
 * The island is a pair of capsules: a WHITE body pill carrying the running clock, a
 * live dot and "Back to call", and a RED "End" pill beside it. Both are 40px tall
 * and fully rounded.
 *
 * The body is a `Card` (`sunken` skin) rendered `component="button"`, not a
 * `<Button>`. The DS button skins are the inverted-black / accent / destructive
 * fills — none of them the white surface this design asks for — and restyling a
 * button's colour is exactly what the DS forbids. A white clickable surface IS a
 * Card, so the colour comes from `--ds-surface-sunken` + `--ds-text-primary` (both
 * dark-mode-ready) and only the capsule geometry is a local override. The one
 * functional mark, the live dot, is Success/600 green — legible on the white
 * capsule where the brand lime would wash out — and carries "live" through its
 * pulse; a muted call holds it steady so the island never claims audio the mic is
 * not delivering.
 *
 * End is the DS `contained` × `destructive` Button — the red "this ends something"
 * skin — now carrying an ✕ and the word "End" rather than an icon alone.
 *
 * ## Two controls, and why they are two ELEMENTS with a gap
 *
 * Hanging up by mistake is unrecoverable — the call, its context and the transcript
 * still being written are gone — so the two must not be one strip a thumb can miss
 * into. They are siblings in a flex row with a real gap: the body's hit area stops
 * at its own edge, the gap belongs to neither, so a near-miss lands on nothing
 * rather than on the destructive control. Shape AND colour differ between them, so
 * the destructive one is never picked by muscle memory for the other.
 *
 * The island renders independently of the agents pill (by request they COEXIST — the pill stays in
 * the header while this floats), so there is no pill↔island morph anymore; it simply appears when the
 * call is minimized and leaves when the call ends.
 */
/**
 * The two-capsule cluster itself — the white body pill (clock · live dot · "Back to
 * call") and the red End pill. Presentational + interactive, so BOTH placements
 * (floating and strip) render the identical control. Assumes the call is minimized;
 * the caller gates on `islandVisible`.
 */
function VoiceCallIslandControls() {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi('voice-call');
  const summary = useVoiceCallSummary();
  const { floatChatIfMinimized } = useActiveConversation();

  if (!summary?.timing.islandVisible) return null;

  const elapsed = formatElapsed(summary.timing.elapsedMs);

  return (
    // The controls sit inside ONE cluster element rather than loose in the anchor,
    // because the morph overlay clones the anchor's innerHTML as the capsule's face:
    // with the row (and its gap) as a real element the clone lays itself out exactly
    // as the live island does, and the shared layer needs no special case for a chip
    // that carries two controls.
    <span className={styles.cluster}>
      <Card
        variant="sunken"
        interactive
        disablePadding
        component="button"
        className={styles.island}
        data-testid="voice-call-island"
        // Announce the destination, not the decoration: the timer is live text a
        // screen reader would otherwise re-read every second.
        aria-label={`${t('voiceCall.expand')} — ${t('voiceCall.duration')} ${elapsed}`}
        onClick={() => {
          logPageClickBi({ page_name: 'voice-call', button_name: 'voice_call_island_expand' });
          // Two moves, in this order: surface the conversation the call belongs to,
          // then restore the call surface over it. Expanding alone would put the
          // call back on top of a collapsed chat, so hanging up would drop the user
          // somewhere they never navigated to.
          floatChatIfMinimized();
          summary.expand();
        }}
      >
        <span className={styles.body}>
          <span className={styles.elapsed} data-testid="voice-call-island-elapsed">
            {elapsed}
          </span>
          <span
            aria-hidden
            className={`${styles.dot}${summary.muted ? ` ${styles.dotMuted}` : ''}`}
            data-testid="voice-call-island-dot"
          />
          <span className={styles.backLabel}>{t('voiceCall.expand')}</span>
        </span>
      </Card>
      <Button
        variant="contained"
        color="destructive"
        size="tiny"
        className={styles.end}
        data-testid="voice-call-island-end"
        aria-label={t('voiceCall.endCall')}
        startIcon={<X size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />}
        onClick={() => {
          logPageClickBi({ page_name: 'voice-call', button_name: 'voice_call_island_end' });
          // The SAME teardown the call surface's End runs (`voice.end`, published
          // on the summary) — not a second route. It drops presence to ended, so
          // `islandVisible` goes false and the island leaves on its own.
          summary.end();
        }}
      >
        {t('voiceCall.end')}
      </Button>
    </span>
  );
}

/**
 * The FLOATING placement (desktop / side-chat): a fixed pill centered at the top,
 * riding above every screen. On mobile web (`narrow`) it renders nothing — the strip
 * (below) owns that width, so the two never both draw.
 */
export function VoiceCallIsland({ narrow = false }: { narrow?: boolean }) {
  const summary = useVoiceCallSummary();

  if (voiceIslandPlacement(narrow, summary?.timing.islandVisible ?? false) !== 'floating') return null;

  return (
    <div
      style={{
        position: 'fixed',
        // Rides below the mobile-web app-download banner when it's up (--app-banner-h,
        // set on :root by AppFrame; 0 otherwise) — this island is fixed to the viewport,
        // so unlike the in-<main> chrome it isn't pushed down for free.
        top: 'calc(env(safe-area-inset-top, 0px) + 32px + var(--app-banner-h, 0px))',
        insetInlineStart: 0,
        insetInlineEnd: 0,
        display: 'flex',
        justifyContent: 'center',
        // Above the chat dock and the nav drawer; the full call surface (z 1000)
        // is never up at the same time, because one hides the other.
        zIndex: 1001,
        pointerEvents: 'none',
      }}
    >
      <span style={{ display: 'inline-flex' }}>
        <VoiceCallIslandControls />
      </span>
    </div>
  );
}

/**
 * The STRIP placement (mobile web): an IN-FLOW band above the app frame that reserves
 * its own height and pushes the header + content down, so the island never floats on
 * top of a screen's header. Background is the page field colour with no separator, so
 * it continues seamlessly into the header/bg below it.
 *
 * It sets `--voice-island-h` on :root while mounted (and clears it on unmount);
 * AppFrame subtracts that from <main>'s minHeight so the strip and <main> sum to the
 * viewport — the same discipline the app-download banner (`--app-banner-h`) uses.
 * Rendered nothing off-narrow (the floating island owns that) and when no call is
 * minimized.
 */
export function VoiceCallStrip({ narrow = false }: { narrow?: boolean }) {
  const summary = useVoiceCallSummary();
  const show = voiceIslandPlacement(narrow, summary?.timing.islandVisible ?? false) === 'strip';

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      '--voice-island-h',
      show ? `calc(${VOICE_STRIP_HEIGHT_PX}px + env(safe-area-inset-top, 0px))` : '0px',
    );
    return () => {
      root.style.removeProperty('--voice-island-h');
    };
  }, [show]);

  if (!show) return null;

  return (
    <div
      className={styles.strip}
      // No landmark role: the strip is a visual container only, and its controls (the island
      // button + End) carry their own labels — a bare role="region" would be an unnamed landmark.
      style={{
        // The band's height is the pill (40) + 12/12 padding = VOICE_STRIP_HEIGHT_PX,
        // plus the top safe-area inset so the pill clears a notch. Same total as the
        // --voice-island-h var AppFrame subtracts from <main>.
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
      }}
    >
      <VoiceCallIslandControls />
    </div>
  );
}
